import { json } from '@plato/shared-util/codecs';
import { captureException } from '@sentry/react';
import type { z } from 'zod';
import type { Callback, ExternalStore } from '../hooks/use-external-store.ts';

/**
 * Thrown when an error is encountered while accessing a value in browser storage.
 */
export class BrowserStorageError extends Error {
    public constructor(message: string, options?: ErrorOptions) {
        super(message, options);
        this.name = 'BrowserStorageError';
    }
}

/**
 * Creates a {@link ExternalStore} instance for use with `useExternalStore`.
 * This should always be created at the module level and not inside a component
 * or hook.
 */
export function createBrowserStoreRaw(
    storage: Storage | undefined,
    key: string,
): ExternalStore<string | undefined> {
    const listeners = new Set<Callback>();
    // This holds values we fail to persist, so getSnapshot still returns the
    // last value we attempted to write.
    let pending: { value: string | undefined } | undefined;

    function notify(): void {
        for (const listener of listeners) {
            listener();
        }
    }

    function onStorage(event: StorageEvent): void {
        if (event.storageArea === storage && event.key === key) {
            pending = undefined;
            notify();
        }
    }

    function getSnapshot(): string | undefined {
        if (pending) {
            return pending.value;
        }
        return storage?.getItem(key) ?? undefined;
    }

    function setValue(value: string | undefined): void {
        if (storage) {
            try {
                if (value === undefined) {
                    storage.removeItem(key);
                } else {
                    storage.setItem(key, value);
                }
                pending = undefined;
            } catch (err) {
                // save the value to pending instead so we can get the value back on
                // the next call to getSnapshot
                pending = { value };
                captureException(new BrowserStorageError(`failed to persist ${key}`, { cause: err }));
            }
        } else {
            pending = { value };
        }
        notify();
    }

    function subscribe(listener: Callback): Callback {
        listeners.add(listener);
        if (typeof window !== 'undefined' && storage && listeners.size === 1) {
            window.addEventListener('storage', onStorage);
        }

        return () => {
            listeners.delete(listener);
            if (typeof window !== 'undefined' && storage && listeners.size === 0) {
                window.removeEventListener('storage', onStorage);
            }
        };
    }

    return { getSnapshot, setValue, subscribe };
}

/**
 * Creates a {@link ExternalStore} instance for use with `useExternalStore`.
 * This should always be created at the module level and not inside a component
 * or hook.
 */
export function createBrowserStore<T>(
    storage: Storage | undefined,
    key: string,
    schema: z.ZodType<T>,
): ExternalStore<T | undefined> {
    const rawStore = createBrowserStoreRaw(storage, key);
    const codec = json(schema);

    let lastSnapshotRaw: string | undefined;
    let lastSnapshotDecoded: T | undefined;

    return {
        getSnapshot: () => {
            const rawValue = rawStore.getSnapshot();

            // always return the same instance for the same source value
            if (rawValue === lastSnapshotRaw) {
                return lastSnapshotDecoded;
            }
            if (rawValue === undefined) {
                lastSnapshotRaw = undefined;
                return;
            }

            lastSnapshotRaw = rawValue;
            const result = codec.safeDecode(rawValue);

            if (result.error) {
                lastSnapshotDecoded = undefined;
                captureException(new BrowserStorageError(`error decoding ${key}`, { cause: result.error }));
                return;
            }

            lastSnapshotDecoded = result.data;
            return result.data;
        },

        setValue: (value) => {
            if (value === undefined) {
                rawStore.setValue(undefined);
            } else {
                rawStore.setValue(codec.encode(value));
            }
        },

        subscribe: rawStore.subscribe.bind(rawStore),
    };
}

/**
 * Creates a {@link ExternalStore} instance for use with `useExternalStore`.
 * This should always be created at the module level and not inside a component
 * or hook.
 */
export function createLocalStorageStore<T>(key: string, schema: z.ZodType<T>): ExternalStore<T | undefined> {
    return createBrowserStore(typeof localStorage === 'undefined' ? undefined : localStorage, key, schema);
}
